/* ===========================================================
   키즈다 뉴스 — 뉴스 리스트 API (searchPostsWithFiles)
   - 모바일(donga_frontend_rebuild) pick_service.getList 패턴 이식
   - 최신순(orderby: DESC) 리스트 + 인기(POPULAR)/추천(RECOMMEND) PICK
   - 응답을 키즈다 뉴스 본문 컴포넌트가 쓰는 article 형태로 정규화
   - API 실패/빈 응답 시 null 반환 → 호출부에서 정적 데이터로 폴백
   =========================================================== */
import graphqlClient from "../../utils/graphql_client_util";
import { topicColorOf } from "./category_api";
import { stripHtmlTags, parseContent } from "../../utils/quill_parser_util";

/* ── AWS Serverless Image Handler (모바일 getTransformedImageUrl 이식) ──
   s3key → CloudFront base64 요청 URL.
   버킷은 모바일과 동일하게 .env의 REACT_APP_ENV("prod" | "dev")로 분기.
   (미지정 시 dev. .env 변경 후에는 dev 서버 재시작 필요) */
const ENV = process.env.REACT_APP_ENV || "dev";
const AWS_S3_CLOUD_FRONT_URL = "https://d3taufrovhgab.cloudfront.net";
const AWS_S3_BUCKET =
  ENV === "prod"
    ? "amplify-d25pugt3kcsz2t-pr-kidsdongapublicdrivebuck-zknojr7gzlqu"
    : "amplify-d25pugt3kcsz2t-de-kidsdongapublicdrivebuck-9lqrxkqmsea1";

/** s3key를 CloudFront 이미지 URL로 변환 (없으면 null)
   - 기본적으로 width를 지정해 대형 원본을 다운사이즈(썸네일이 깨지거나 과도하게 큰 것 방지) */
export function s3KeyToImageUrl(s3key, resize = { width: 1200, fit: "cover" }) {
  if (!s3key || typeof s3key !== "string") return null;
  try {
    const key = s3key.replace(/^\/+/, "");
    const req = JSON.stringify({
      bucket: AWS_S3_BUCKET,
      key,
      edits: { resize },
    });
    // 브라우저 btoa는 UTF-8을 직접 처리하지 못하므로 인코딩 후 변환
    const b64 = btoa(unescape(encodeURIComponent(req)));
    return `${AWS_S3_CLOUD_FRONT_URL}/${b64}`;
  } catch (e) {
    console.error("s3KeyToImageUrl error:", e);
    return null;
  }
}

/* ARGB/RGB hex("FFFF7520") → CSS hex("#FF7520"). 기본 브랜드 블루 */
function toCssColor(raw) {
  if (!raw || typeof raw !== "string") return "#155dfc";
  const hex = raw.replace(/[^0-9a-fA-F]/g, "");
  const rgb = hex.length >= 8 ? hex.slice(-6) : hex.length === 6 ? hex : null;
  return rgb ? `#${rgb}` : "#155dfc";
}

/* 문자열에서 안정적인 hue(0~359) 생성 — 이미지 없을 때 Thumb 플레이스홀더 색 */
function hueOf(str) {
  const s = str || "";
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
}

/* "yyyy-MM-dd ..." / ISO → "yyyy-MM-dd" */
function toDateOnly(s) {
  if (!s || typeof s !== "string") return "";
  return s.slice(0, 10);
}

/* content → 요약 텍스트
   - Quill Delta(JSON 배열) 형식이면 문자열 insert만 이어붙이고 이미지/비디오(object insert)는 제외
   - HTML/평문이면 태그 제거
   - 공백/개행은 한 칸으로 정리 */
function contentToText(content) {
  if (!content || typeof content !== "string") return "";
  const trimmed = content.trim();
  if (trimmed.startsWith("[")) {
    try {
      const delta = JSON.parse(trimmed);
      if (Array.isArray(delta)) {
        let text = "";
        for (const op of delta) {
          // 이미지/비디오 등 object insert는 건너뛰고 텍스트만 추출
          if (op && typeof op.insert === "string") text += op.insert;
        }
        return text.replace(/\s+/g, " ").trim();
      }
    } catch (e) {
      /* JSON 파싱 실패 시 HTML/평문 처리로 폴백 */
    }
  }
  return stripHtmlTags(trimmed).replace(/\s+/g, " ").trim();
}

/* tags: 배열 또는 "a,b c" 문자열 모두 허용 → 문자열 배열 */
function normalizeTags(tags) {
  if (Array.isArray(tags)) return tags.filter(Boolean);
  if (typeof tags === "string" && tags.trim()) {
    return tags
      .split(/[,#\s]+/)
      .map((t) => t.trim())
      .filter(Boolean);
  }
  return [];
}

/* 평일/오전 6시 기준 기사 노출일 (모바일 getRelevantDate 이식) */
export function getRelevantDate() {
  let now = new Date();
  const threshold = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 6);
  if (now < threshold) now.setDate(now.getDate() - 1);
  while (now.getDay() === 0 || now.getDay() === 6) {
    now.setDate(now.getDate() - 1);
  }
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const NEWS_LIST_QUERY = `
  query SearchPostsWithFiles($input: PostSearchInput!) {
    searchPostsWithFiles(input: $input) {
      totalPages
      totalElements
      success
      size
      page
      error {
        code
        errorType
        message
      }
      data {
        files {
          s3key
        }
        categoryTitle
        categoryColor
        categorySeq
        topicCategorySeq
        topicCategoryTitle
        tags
        title
        postSeq
        content
        newsDate
        regDate
        detailType
        type
        reporters {
          name
        }
        scrapCount
        likeCount
        webReadCount
      }
    }
  }
`;

/* searchPostsWithFiles 호출 시 항상 outputType: LANDING_WEB 를 강제 주입.
   (웹 랜딩 전용 출력 형태를 보장 — 호출부에서 누락돼도 항상 포함됨) */
const withOutputType = (input) => ({ ...input, outputType: "LANDING_WEB" });

/** searchPostsWithFiles의 단일 item을 웹 article 형태로 정규화 */
function toArticle(item) {
  // 웹 노출 라벨 = 1차(뉴스 주제)만 — 2차(키즈다 카테고리)는 웹 미노출 (BR-09)
  const topicTitle = item.topicCategoryTitle || "";
  const rawSummary = contentToText(item.content);
  return {
    id: String(item.postSeq),
    // API 기사임을 나타내는 표시 — 컴포넌트의 catInfo()가 우선 사용
    apiCategoryName: topicTitle,
    apiCategoryColor: topicColorOf(topicTitle),
    categorySeq: item.categorySeq ?? null,
    topicCategorySeq: item.topicCategorySeq ?? null,
    corner: null,
    title: item.title || "",
    summary: rawSummary ? rawSummary.slice(0, 140) : "",
    author: item.reporters?.[0]?.name || "키즈다 편집부",
    date: toDateOnly(item.newsDate || item.regDate),
    // 웹은 웹 전용 조회수(webReadCount)만 사용 (readCount 미사용)
    views: item.webReadCount ?? 0,
    likes: item.likeCount || 0,
    pick: false,
    headline: false,
    tags: normalizeTags(item.tags),
    image: s3KeyToImageUrl(item.files?.[0]?.s3key),
    hue: hueOf(topicTitle || item.title),
  };
}

/* ── 검색 컨텍스트 태깅 ──
   각 리스트가 어떤 postSearchType(+topicCategorySeq)으로 조회됐는지를 기사에 심어,
   상세페이지 진입 시 쿼리스트링으로 전달 → postPrevNext(함께 읽어요)가 같은
   컨텍스트에서 이전/다음 기사를 조회한다.
   - postSearchType: NEWS_ALL | POPULAR | RECOMMEND | NEWS_CATEGORY
   - searchTopicCategorySeq: 1차 카테고리 리스트일 때만 (기사 자체의 topicCategorySeq와 구분) */
function withCtx(items, ctx) {
  if (!Array.isArray(items)) return items;
  return items.map((a) => ({ ...a, ...ctx }));
}

/* 공통 호출 헬퍼 — 실패/빈 응답 시 null */
async function fetchPosts(input) {
  const data = await graphqlClient.query(
    NEWS_LIST_QUERY,
    { input: withOutputType(input) },
    { useAuth: false }
  );
  const res = data?.searchPostsWithFiles;
  if (res?.success && Array.isArray(res.data) && res.data.length) {
    return res.data.map(toArticle);
  }
  return null;
}

/**
 * 최신순 뉴스 리스트 — "키즈다 뉴스" 섹션 메인
 * @param {{size?: number, page?: number}} opts
 * @returns {Promise<Array|null>}  정규화된 article 배열 | null(폴백)
 */
export const getNewsList = async ({ size = 12, page = 0, date = "" } = {}) => {
  try {
    // 앱 news_all_service.getList 와 동일: 전체 뉴스(NEWS_ALL) 최신순
    const input = {
      postSearchType: "NEWS_ALL",
      orderby: "DESC",
      size,
      page,
      // endDate만 지정 → 선택일 이하에서 최신순.
      // 선택일에 데이터가 없으면 그 이전 날짜 기사가 자연히 따라온다(서버 처리).
      endDate: date || getRelevantDate(),
    };
    return withCtx(await fetchPosts(input), { postSearchType: "NEWS_ALL" });
  } catch (err) {
    console.error("getNewsList error:", err);
    return null;
  }
};

/**
 * 키즈다 PICK — 인기(POPULAR) / 추천(RECOMMEND)
 * @param {"popular"|"recommend"} kind
 * @param {{size?: number, page?: number}} opts
 * @returns {Promise<Array|null>}
 */
export const getPickNews = async (kind, { size = 8, page = 0, date = "" } = {}) => {
  try {
    const postSearchType = kind === "recommend" ? "RECOMMEND" : "POPULAR";
    const items = await fetchPosts({
      orderby: "DESC",
      size,
      page,
      newsDate: date || getRelevantDate(),
      postSearchType,
    });
    if (!items) return null;
    return items.map((a) => ({ ...a, pick: kind === "recommend", postSearchType }));
  } catch (err) {
    console.error("getPickNews error:", err);
    return null;
  }
};

/**
 * 1차 카테고리별 뉴스 — 웹은 1차만 사용 (BR-09)
 * (postSearchType: NEWS_ALL + topicCategorySeq(1차), endDate=날짜/기본 noted date, 최신순)
 * @param {{topicCategorySeq: number|string, size?: number, page?: number, date?: string}} opts
 * @returns {Promise<Array|null>}  정규화된 article 배열 | null
 */
export const getNewsByCategory = async ({ topicCategorySeq, size = 6, page = 0, date = "" } = {}) => {
  try {
    const input = {
      postSearchType: "NEWS_ALL",
      orderby: "DESC",
      size,
      page,
      endDate: date || getRelevantDate(),
    };
    // topicCategorySeq(1차)는 유효한 숫자일 때만 input에 추가 (null/빈값/비숫자 키는 제외)
    const _seq = parseInt(topicCategorySeq, 10);
    if (Number.isFinite(_seq)) {
      input.topicCategorySeq = _seq;
    }
    return withCtx(
      await fetchPosts(input),
      Number.isFinite(_seq)
        ? { postSearchType: "NEWS_ALL", searchTopicCategorySeq: _seq }
        : { postSearchType: "NEWS_ALL" }
    );
  } catch (err) {
    console.error("getNewsByCategory error:", err);
    return null;
  }
};

/**
 * 카테고리별 뉴스 — 페이지네이션 (카테고리 전용 페이지용, 날짜 무관 최신순)
 * @param {{topicCategorySeq: number|string, size?: number, page?: number}} opts (page는 0-based)
 * @returns {Promise<{items: Array, totalPages: number, totalElements: number, page: number}|null>}
 */
export const getNewsByCategoryPaged = async ({ topicCategorySeq, size = 20, page = 0 } = {}) => {
  try {
    const input = {
      postSearchType: "NEWS_ALL",
      orderby: "DESC",
      size,
      page,
      endDate: getRelevantDate(),
    };
    // topicCategorySeq(1차)는 유효한 숫자일 때만 input에 추가 (null/빈값/비숫자 키는 제외)
    const _seq = parseInt(topicCategorySeq, 10);
    if (Number.isFinite(_seq)) {
      input.topicCategorySeq = _seq;
    }

    const data = await graphqlClient.query(
      NEWS_LIST_QUERY,
      { input: withOutputType(input) },
      { useAuth: false }
    );
    const res = data?.searchPostsWithFiles;
    if (res?.success && Array.isArray(res.data)) {
      const totalElements = res.totalElements ?? res.data.length;
      const totalPages =
        res.totalPages ?? Math.max(1, Math.ceil(totalElements / size));
      return {
        items: withCtx(
          res.data.map(toArticle),
          Number.isFinite(_seq)
            ? { postSearchType: "NEWS_ALL", searchTopicCategorySeq: _seq }
            : { postSearchType: "NEWS_ALL" }
        ),
        totalPages,
        totalElements,
        page: res.page ?? page,
      };
    }
    return null;
  } catch (err) {
    console.error("getNewsByCategoryPaged error:", err);
    return null;
  }
};

/**
 * 제목 검색 — 공통 searchPostsWithFiles 쿼리 재사용
 * input: { postSearchType: NEWS_ALL, orderby: DESC, page, size, title }
 * @param {{query: string, size?: number, page?: number}} opts (page는 0-based)
 * @returns {Promise<{items: Array, totalPages: number, totalElements: number, page: number}|null>}
 */
export const getNewsSearch = async ({ query, size = 20, page = 0 } = {}) => {
  try {
    const input = {
      postSearchType: "NEWS_ALL",
      orderby: "DESC",
      size,
      page,
      title: query,
    };
    const data = await graphqlClient.query(
      NEWS_LIST_QUERY,
      { input: withOutputType(input) },
      { useAuth: false }
    );
    const res = data?.searchPostsWithFiles;
    if (res?.success && Array.isArray(res.data)) {
      const totalElements = res.totalElements ?? res.data.length;
      const totalPages =
        res.totalPages ?? Math.max(1, Math.ceil(totalElements / size));
      return {
        // searchQuery: 상세 진입 시 검색어를 쿼리스트링(q)으로 넘겨
        // 상단 검색란 입력값을 유지하기 위한 컨텍스트
        items: withCtx(res.data.map(toArticle), {
          postSearchType: "NEWS_ALL",
          searchQuery: query,
        }),
        totalPages,
        totalElements,
        page: res.page ?? page,
      };
    }
    return null;
  } catch (err) {
    console.error("getNewsSearch error:", err);
    return null;
  }
};

/**
 * 데이터가 있는 가장 가까운 날짜를 확정 (선택일 또는 그 이전)
 * endDate=date 로 1건만 조회해 가장 최신 기사 날짜를 돌려준다.
 * @param {string} date - YYYY-MM-DD (빈 값이면 기본 noted date)
 * @returns {Promise<string>}  "YYYY-MM-DD" | "" (데이터 없음/실패)
 */
export const resolveNewsDate = async (date = "") => {
  try {
    const items = await fetchPosts({
      postSearchType: "NEWS_ALL",
      orderby: "DESC",
      size: 1,
      page: 0,
      endDate: date || getRelevantDate(),
    });
    return items && items.length ? items[0].date : "";
  } catch (err) {
    console.error("resolveNewsDate error:", err);
    return "";
  }
};

/**
 * 특정 날짜의 전체 뉴스 — 페이지네이션 (날짜별 전체 페이지용)
 * 그 날짜(startDate=endDate=date)만 조회해 totalPages/totalElements 가 그 날짜 기준이 되게 한다.
 * @param {{date?: string, size?: number, page?: number}} opts (page는 0-based)
 * @returns {Promise<{items: Array, totalPages: number, totalElements: number, page: number}|null>}
 */
export const getNewsAllPaged = async ({ date = "", size = 20, page = 0 } = {}) => {
  try {
    const input = {
      postSearchType: "NEWS_ALL",
      orderby: "DESC",
      size,
      page,
    };
    if (date) {
      input.startDate = date;
      input.endDate = date;
    } else {
      input.endDate = getRelevantDate();
    }

    const data = await graphqlClient.query(
      NEWS_LIST_QUERY,
      { input: withOutputType(input) },
      { useAuth: false }
    );
    const res = data?.searchPostsWithFiles;
    if (res?.success && Array.isArray(res.data)) {
      const totalElements = res.totalElements ?? res.data.length;
      const totalPages =
        res.totalPages ?? Math.max(1, Math.ceil(totalElements / size));
      return {
        items: withCtx(res.data.map(toArticle), { postSearchType: "NEWS_ALL" }),
        totalPages,
        totalElements,
        page: res.page ?? page,
      };
    }
    return null;
  } catch (err) {
    console.error("getNewsAllPaged error:", err);
    return null;
  }
};

/* ── 이전/다음 기사 (postPrevNext) — "이 기사와 함께 읽어요" ──
   리스트에서 넘어온 컨텍스트(postSearchType + topicCategorySeq)와 동일한 조건으로
   현재 기사(postSeq)의 이전/다음 기사를 조회한다. */
const PREV_NEXT_ITEM_FIELDS = `
  postSeq
  title
  content
  tags
  newsDate
  regDate
  categorySeq
  categoryTitle
  categoryColor
  topicCategorySeq
  topicCategoryTitle
  webReadCount
  likeCount
  files {
    s3key
  }
  reporters {
    name
  }
`;

/* postSearchType 화이트리스트 — enum을 쿼리에 인라인하므로 검증 필수 */
const VALID_SEARCH_TYPES = ["NEWS_ALL", "POPULAR", "RECOMMEND", "NEWS_CATEGORY"];

/**
 * 이전/다음 기사 조회 (postPrevNext)
 * @param {string|number} postSeq - 현재 기사
 * @param {{postSearchType?: string, topicCategorySeq?: number|string}} ctx - 리스트 컨텍스트
 * @returns {Promise<{prev: Object|null, next: Object|null}|null>} 정규화된 article | null(실패)
 */
export const getPostPrevNext = async (postSeq, { postSearchType = "NEWS_ALL", topicCategorySeq } = {}) => {
  try {
    const seq = parseInt(postSeq, 10);
    if (Number.isNaN(seq)) return null;

    const type = VALID_SEARCH_TYPES.includes(postSearchType) ? postSearchType : "NEWS_ALL";
    const _catSeq = parseInt(topicCategorySeq, 10);
    // 1차(topicCategorySeq) 필터 — 값은 parseInt 로 숫자 강제 후 인라인 (인젝션 차단)
    const catPart = Number.isFinite(_catSeq) ? `, topicCategorySeq: ${_catSeq}` : "";

    // input 타입명이 스키마마다 다를 수 있어 변수 선언 대신 검증된 값을 인라인
    const query = `
      query PostPrevNext {
        postPrevNext(
          input: { postSearchType: ${type}${catPart}, outputType: LANDING_WEB }
          postSeq: ${seq}
        ) {
          success
          error {
            code
            errorType
            message
          }
          data {
            prev {
              ${PREV_NEXT_ITEM_FIELDS}
            }
            next {
              ${PREV_NEXT_ITEM_FIELDS}
            }
          }
        }
      }
    `;

    const data = await graphqlClient.query(query, {}, { useAuth: false });
    const res = data?.postPrevNext;
    if (res?.success && res.data) {
      // 같은 컨텍스트를 다시 심어 → 이전/다음 기사로 이동해도 체인 유지
      const ctx = {
        postSearchType: type,
        ...(Number.isFinite(_catSeq) ? { searchTopicCategorySeq: _catSeq } : {}),
      };
      const normalize = (item) =>
        item && item.postSeq != null ? { ...toArticle(item), ...ctx } : null;
      return { prev: normalize(res.data.prev), next: normalize(res.data.next) };
    }
    return null;
  } catch (err) {
    console.error("getPostPrevNext error:", err);
    return null;
  }
};

/* ── 기사 상세 (postsBySeq) ──
   모바일 item_detail_widget.dart 의 getPost(=postsBySeq) 패턴 이식.
   웹은 비로그인 기준이라 userSeq 는 보내지 않는다. */
const POST_DETAIL_QUERY = `
  query PostsBySeq($postSeq: Int!) {
    postsBySeq(postSeq: $postSeq) {
      success
      error {
        code
        errorType
        message
      }
      data {
        postSeq
        title
        content
        summary
        tags
        newsDate
        regDate
        regName
        webReadCount
        likeCount
        categoryTitle
        categoryColor
        categorySeq
        topicCategorySeq
        topicCategoryTitle
        reporters {
          name
        }
        files {
          s3key
        }
      }
    }
  }
`;

/**
 * 기사 상세 조회 (postsBySeq)
 * @param {string|number} postSeq
 * @returns {Promise<Object|null>}  ArticlePage용 article 객체(body 포함) | null
 */
export const getNewsDetail = async (postSeq) => {
  try {
    const seq = parseInt(postSeq, 10);
    if (Number.isNaN(seq)) return null;

    const data = await graphqlClient.query(
      POST_DETAIL_QUERY,
      { postSeq: seq },
      { useAuth: false }
    );

    const res = data?.postsBySeq;
    if (res?.success && res.data) {
      const item = res.data;
      const base = toArticle(item); // id/카테고리/날짜/태그/이미지/요약 정규화 재사용
      return {
        ...base,
        // 원본 Delta 보존 → ArticlePage가 Quill로 동일 스타일 렌더
        content: item.content || "",
        // 폴백용: Delta(또는 HTML/평문) → HTML
        body: parseContent(item.content || ""),
      };
    }
    return null;
  } catch (err) {
    console.error("getNewsDetail error:", err);
    return null;
  }
};

/* ── 웹 조회수 기록 (createWebPostRead) ──
   기사 상세 진입 시 호출해 웹 전용 조회수(webReadCount)를 1 증가시킨다.
   비로그인 기준이라 인증 토큰 없이 호출. */
const CREATE_WEB_POST_READ = `
  mutation CreateWebPostRead($input: PostWebReadCreateInput!) {
    createWebPostRead(input: $input) {
      success
      error {
        code
        errorType
        message
      }
      data {
        message
        success
      }
    }
  }
`;

/**
 * 웹 조회수 기록
 * @param {string|number} postSeq
 * @returns {Promise<Object|null>} createWebPostRead 응답 | null(유효하지 않은 seq/실패)
 */
export const createWebPostRead = async (postSeq) => {
  try {
    const seq = parseInt(postSeq, 10);
    if (Number.isNaN(seq)) return null;

    const data = await graphqlClient.mutation(
      CREATE_WEB_POST_READ,
      { input: { postSeq: seq } },
      { useAuth: false }
    );
    return data?.createWebPostRead ?? null;
  } catch (err) {
    console.error("createWebPostRead error:", err);
    return null;
  }
};
